import { api } from '../services/api';

const CONSENT_KEY = 'cookie-consent';
const CONSENT_DATE_KEY = 'cookie-consent-date';

export type ConsentValue = 'accepted' | 'essential-only';

/** Read the current consent value from localStorage */
export function getConsentValue(): ConsentValue | null {
  return localStorage.getItem(CONSENT_KEY) as ConsentValue | null;
}

/** Check if the user has accepted analytics/advertising cookies */
export function hasAnalyticsConsent(): boolean {
  return getConsentValue() === 'accepted';
}

function isAuthenticated(): boolean {
  const stored = localStorage.getItem('weather-story-auth');
  if (!stored) return false;
  try {
    const { state } = JSON.parse(stored);
    return !!state?.accessToken;
  } catch {
    return false;
  }
}

/** Update Google Consent Mode v2 based on user's choice */
function updateGoogleConsentMode(value: ConsentValue) {
  const gtag = (window as unknown as Record<string, (...args: unknown[]) => void>).gtag;
  if (typeof gtag !== 'function') return;

  const granted = value === 'accepted';
  gtag('consent', 'update', {
    ad_storage: granted ? 'granted' : 'denied',
    ad_user_data: granted ? 'granted' : 'denied',
    ad_personalization: granted ? 'granted' : 'denied',
    analytics_storage: granted ? 'granted' : 'denied',
  });
}

/** Save consent choice to localStorage and sync to server if authenticated */
export function setConsent(value: ConsentValue) {
  localStorage.setItem(CONSENT_KEY, value);
  localStorage.setItem(CONSENT_DATE_KEY, new Date().toISOString());
  window.dispatchEvent(new CustomEvent('cookie-consent-change', { detail: value }));
  updateGoogleConsentMode(value);

  if (isAuthenticated()) {
    syncConsentToServer(value);
  }
}

/** Clear consent (re-shows the banner) */
export function clearConsent() {
  localStorage.removeItem(CONSENT_KEY);
  localStorage.removeItem(CONSENT_DATE_KEY);
  window.dispatchEvent(new CustomEvent('cookie-consent-change', { detail: null }));
  updateGoogleConsentMode('essential-only');
}

/** Sync local consent to server (fire-and-forget) */
export function syncConsentToServer(value: ConsentValue) {
  api.put('/users/me/cookie-consent', { consent: value, source: 'web' }).catch(() => {});
}

/** Fetch server consent and apply locally (used after login) */
export async function syncConsentFromServer() {
  try {
    const response = await api.get<{
      cookieConsent: string | null;
      cookieConsentDate: string | null;
    }>('/users/me/cookie-consent');
    const serverConsent = response.data?.cookieConsent as ConsentValue | null;
    const localConsent = getConsentValue();

    if (serverConsent && !localConsent) {
      // Server has consent but local doesn't — apply server value
      localStorage.setItem(CONSENT_KEY, serverConsent);
      localStorage.setItem(
        CONSENT_DATE_KEY,
        response.data?.cookieConsentDate || new Date().toISOString()
      );
      window.dispatchEvent(new CustomEvent('cookie-consent-change', { detail: serverConsent }));
      updateGoogleConsentMode(serverConsent);
    } else if (localConsent && !serverConsent) {
      // Local has consent but server doesn't — push local to server
      syncConsentToServer(localConsent);
    }
  } catch {
    // Silent fail
  }
}
